// Shared image utilities: resize, upload to Supabase, fetch from URL
// Used by ImageUploadField and SecondaryImagesList components

import { supabase } from '@/lib/supabase';
import { getWorkerApiBase } from '@/lib/worker-api';

const BUCKET = 'store-assets';

// Target size range for resized images (cover photos)
const MIN_SIZE_BYTES = 500 * 1024; // 500KB
const MAX_SIZE_BYTES = 1024 * 1024; // 1MB

export type ImageFolder = 'categories' | 'families' | 'items' | 'brands';

/**
 * Resize an image blob to fit within target size range (500KB-1MB).
 * Uses canvas to progressively reduce quality/dimensions.
 */
export async function resizeImageToTargetSize(
  blob: Blob,
  minBytes: number = MIN_SIZE_BYTES,
  maxBytes: number = MAX_SIZE_BYTES
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const width = img.naturalWidth;
      const height = img.naturalHeight;
      
      // If image is already in target range, return as-is
      if (blob.size >= minBytes && blob.size <= maxBytes) {
        resolve(blob);
        return;
      }

      // Start with original dimensions, reduce if needed
      const quality = 0.92;
      let scaleFactor = 1.0;
      
      // For very large images (>5MB), start with smaller dimensions
      if (blob.size > 5 * 1024 * 1024) {
        scaleFactor = Math.sqrt((2 * 1024 * 1024) / blob.size);
      } else if (blob.size > 2 * 1024 * 1024) {
        scaleFactor = 0.8;
      }

      const tryResize = (scale: number, q: number): Promise<Blob> => {
        return new Promise((res, rej) => {
          const w = Math.round(width * scale);
          const h = Math.round(height * scale);
          canvas.width = w;
          canvas.height = h;
          ctx.clearRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          
          canvas.toBlob(
            (result) => {
              if (result) res(result);
              else rej(new Error('Failed to create blob'));
            },
            'image/jpeg',
            q
          );
        });
      };

      // Binary search for optimal quality/scale
      const findOptimalSize = async (): Promise<Blob> => {
        let bestBlob: Blob | null = null;
        let currentScale = scaleFactor;
        let currentQuality = quality;
        let attempts = 0;
        const maxAttempts = 15;

        while (attempts < maxAttempts) {
          attempts++;
          const result = await tryResize(currentScale, currentQuality);
          
          if (result.size >= minBytes && result.size <= maxBytes) {
            // Perfect! In target range
            return result;
          }
          
          if (result.size > maxBytes) {
            // Too big, reduce quality or scale
            if (currentQuality > 0.5) {
              currentQuality -= 0.1;
            } else {
              currentScale *= 0.85;
              currentQuality = 0.85;
            }
          } else {
            // Too small (below minBytes), increase quality or scale
            if (currentQuality < 0.95 && result.size < minBytes) {
              currentQuality += 0.05;
            } else {
              // Accept it if it's reasonably sized (at least 200KB)
              if (result.size > 200 * 1024) {
                return result;
              }
              // Otherwise try larger scale
              currentScale = Math.min(currentScale * 1.1, 1.0);
            }
          }
          
          bestBlob = result;
        }

        // Return best attempt
        return bestBlob || blob;
      };

      findOptimalSize().then(resolve).catch(reject);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(blob);
  });
}

/**
 * Upload a blob to Supabase storage and return the public URL.
 */
export async function uploadBlobToSupabase(
  blob: Blob,
  folder: ImageFolder,
  extension: string = 'jpg'
): Promise<string> {
  const path = `${folder}/${crypto.randomUUID()}.${extension}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, blob, {
    cacheControl: '3600',
    upsert: false,
    contentType: blob.type || 'image/jpeg',
  });
  if (error) throw error;
  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return urlData.publicUrl;
}

/**
 * Get the proxy URL for fetching external images.
 * In development (Vite), we use the wrangler dev server.
 * In production, we use the same origin.
 */
function getProxyUrl(imageUrl: string): string {
  const encodedUrl = encodeURIComponent(imageUrl);
  return `${getWorkerApiBase()}/api/proxy-image?url=${encodedUrl}`;
}

/**
 * Check if a URL points to an image based on file extension.
 * Used as a fallback when Content-Type is unreliable.
 */
function hasImageExtension(url: string): boolean {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif'];
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    const ext = pathname.split('.').pop() || '';
    return imageExtensions.includes(ext);
  } catch {
    return false;
  }
}

/**
 * Fetch an image from URL (via proxy to bypass CORS), resize it, and upload to Supabase.
 * Returns the public URL of the uploaded image.
 */
export async function fetchResizeAndUpload(
  url: string,
  folder: ImageFolder
): Promise<string> {
  // First try direct fetch (works for same-origin or CORS-enabled URLs)
  let response: Response;
  let usedProxy = false;
  
  try {
    response = await fetch(url, { mode: 'cors' });
  } catch {
    // Direct fetch failed due to CORS, try proxy
    try {
      const proxyUrl = getProxyUrl(url);
      response = await fetch(proxyUrl);
      usedProxy = true;
    } catch {
      throw new Error('Cannot fetch image. Check the URL or try downloading manually.');
    }
  }

  if (!response.ok) {
    // If direct fetch returned error, try proxy
    if (!usedProxy) {
      try {
        const proxyUrl = getProxyUrl(url);
        response = await fetch(proxyUrl);
        usedProxy = true;
      } catch {
        throw new Error('Cannot fetch image. Check the URL or try downloading manually.');
      }
    }
    
    if (!response.ok) {
      const fallbackMsg = `No se pudo obtener la imagen (${response.status}). Descargue el archivo y súbalo manualmente.`;
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const errorData = (await response.json()) as { error?: string };
        throw new Error(errorData.error || fallbackMsg);
      }
      throw new Error(fallbackMsg);
    }
  }

  const blob = await response.blob();
  
  // Validate that the response is an image.
  // blob.type comes from Content-Type header, which may be empty/incorrect for some servers.
  // If proxy was used, it already validated the content, so we trust it.
  // As a fallback, also check if the URL has an image extension.
  const isImageByType = blob.type.startsWith('image/');
  const isImageByExtension = hasImageExtension(url);
  
  if (!isImageByType && !usedProxy && !isImageByExtension) {
    throw new Error('URL does not point to an image');
  }

  // Resize to target range (500KB-1MB)
  const resizedBlob = await resizeImageToTargetSize(blob);
  
  // Upload to Supabase
  return uploadBlobToSupabase(resizedBlob, folder, 'jpg');
}

/**
 * Upload a file directly to Supabase (no resize).
 */
export async function uploadFileToSupabase(
  file: File,
  folder: ImageFolder
): Promise<string> {
  const ext = file.name.split('.').pop() || 'jpg';
  return uploadBlobToSupabase(file, folder, ext);
}
